import Card from "components/card";
import { cn } from "components/ui/cn";

/**
 * Comparación contra el mismo período del año anterior.
 * `mejorCuando` existe porque no todo mejora subiendo: más partidos es bueno,
 * más cancelados no.
 */
export interface WidgetComparacion {
  delta: number;
  etiqueta: string;
  mejorCuando?: "sube" | "baja";
}

/**
 * Tarjeta de métrica (KPI).
 *
 * Se invirtió la jerarquía: primero el rótulo en tamaño chico y color apagado,
 * después el dato en grande. Antes el icono ocupaba un círculo de 90px de alto
 * que pesaba más que la cifra que la tarjeta viene a comunicar; ahora es una
 * marca discreta arriba a la derecha.
 */
const Widget = (props: {
  icon: JSX.Element;
  title: string;
  subtitle: React.ReactNode;
  comparacion?: WidgetComparacion | null;
}) => {
  const { icon, title, subtitle, comparacion } = props;

  const mejorCuando = comparacion?.mejorCuando ?? "sube";
  const delta = comparacion?.delta ?? 0;
  const esMejor = mejorCuando === "sube" ? delta > 0 : delta < 0;
  const esPeor = mejorCuando === "sube" ? delta < 0 : delta > 0;

  return (
    <Card extra="group p-3.5 sm:p-5" interactive>
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 text-[12px] font-medium leading-snug text-gray-700 dark:text-gray-400 sm:text-[13px]">
          {title}
        </p>
        <span className="flex shrink-0 items-center text-gray-600 transition-colors duration-200 group-hover:text-brand-500 dark:text-gray-500 dark:group-hover:text-brand-200">
          {icon}
        </span>
      </div>

      <div className="tabular mt-2 break-words text-[19px] font-bold leading-tight text-gray-900 dark:text-white sm:mt-3 sm:text-[22px]">
        {subtitle}
      </div>

      {comparacion && (
        <p
          className={cn(
            "tabular mt-1.5 flex items-center gap-1 text-[11px] font-medium leading-snug sm:text-[12px]",
            esMejor && "text-green-700 dark:text-green-400",
            esPeor && "text-red-600 dark:text-red-400",
            !esMejor && !esPeor && "text-gray-700 dark:text-gray-500"
          )}
        >
          {delta === 0 ? (
            <>Igual que {comparacion.etiqueta}</>
          ) : (
            <>
              <span aria-hidden="true">{delta > 0 ? "▲" : "▼"}</span>
              {Math.abs(delta)} vs. {comparacion.etiqueta}
            </>
          )}
        </p>
      )}
    </Card>
  );
};

export default Widget;
